import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, type Me, type LicenseRow, type LicenseEventRow, type PaymentSessionRow, type TenantRow } from '../lib/api';

function fmtUSD(cents: number, ccy = 'USD') {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy }).format(cents / 100);
}
function daysUntil(ms: number): number { return Math.round((ms - Date.now()) / 86400000); }
function fmtDate(ms: number | null | undefined) { return ms ? new Date(ms).toLocaleDateString() : '—'; }

export default function License({ me }: { me: Me }) {
  const [params, setParams] = useSearchParams();
  const tenantParam = params.get('tenant_id');
  // Super-admin has no license of their own (tenant_id='GLOBAL'). If they
  // arrive without ?tenant_id, render a tenant picker instead of pulling
  // /licenses/mine which would 404.
  const needsPicker = me.role === 'super_admin' && !tenantParam;
  const tenant_id = (me.role === 'super_admin' && tenantParam) ? tenantParam : me.tenant_id;
  const [license, setLicense] = useState<LicenseRow | null>(null);
  const [events, setEvents] = useState<LicenseEventRow[]>([]);
  const [sessions, setSessions] = useState<PaymentSessionRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [xero, setXero] = useState<{ connected: boolean; tenant_name?: string } | null>(null);
  const [allTenants, setAllTenants] = useState<TenantRow[] | null>(null);

  const psParam = params.get('ps');
  const statusParam = params.get('status');

  async function load() {
    if (needsPicker) {
      // Don't try to load license data when no tenant is selected — just
      // populate the picker.
      try {
        const r = await api.tenantsList();
        setAllTenants(r.tenants);
      } catch (e: any) { setErr(e.message); }
      return;
    }
    try {
      setErr(null);
      const [lics, ps, xs] = await Promise.all([
        api.tenantsListLicenses(tenant_id),
        api.paymentSessions(tenant_id).catch(() => ({ sessions: [] })),
        me.role === 'super_admin' ? api.xeroStatus().catch(() => null) : Promise.resolve(null),
      ]);
      const active = lics.licenses.find((l) => l.status === 'active') ?? lics.licenses[0] ?? null;
      setLicense(active); setEvents(lics.events); setSessions(ps.sessions);
      if (xs) setXero(xs);
    } catch (e: any) { setErr(e.message); }
  }
  useEffect(() => { load(); }, [tenant_id, needsPicker]);

  // ─── Super-admin tenant picker ───
  if (needsPicker) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">License</h1>
          <p className="text-gray-500 text-sm mt-1">
            Pick a tenant to view or manage its license, renewals, and Xero invoices.
          </p>
        </div>
        {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
        {allTenants === null && !err && <div className="card p-8 text-center text-gray-500">Loading tenants…</div>}
        {allTenants && allTenants.length === 0 && (
          <div className="card p-8 text-center text-gray-500">No tenants yet.</div>
        )}
        {allTenants && allTenants.length > 0 && (
          <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark">
            {allTenants.map((t) => (
              <button
                key={t.tenant_id}
                onClick={() => setParams({ tenant_id: t.tenant_id })}
                className="w-full text-left px-4 py-3 hover:bg-tnh-accent/[0.04] transition flex items-center justify-between"
              >
                <div>
                  <div className="font-mono font-medium text-sm">{t.tenant_id}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {t.name} · {t.billing_email || 'no billing email'} · {t.subscription_tier}
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {t.end_date ? `expires ${fmtDate(t.end_date)}` : 'no expiration'} →
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  const canRenew = me.role === 'super_admin' || (me.role === 'tenant_admin' && me.tenant_id === tenant_id);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">License</h1>
        <p className="text-gray-500 text-sm mt-1">
          Subscription window, enabled modules, and renewal. Charges go through Stripe; receipts are mirrored to Xero.
        </p>
      </div>

      {psParam && statusParam === 'success' && (
        <div className="card p-4 border-tnh-ok bg-emerald-50 dark:bg-emerald-900/20 text-sm">
          Payment received for session <span className="font-mono">{psParam}</span>. Your license has been extended.
          <button onClick={() => setParams({})} className="ml-3 text-xs text-tnh-accent hover:underline">dismiss</button>
        </div>
      )}
      {psParam && statusParam === 'cancel' && (
        <div className="card p-4 border-tnh-warn bg-amber-50 dark:bg-amber-900/20 text-sm">
          Payment cancelled. Nothing was charged.
          <button onClick={() => setParams({})} className="ml-3 text-xs text-tnh-accent hover:underline">dismiss</button>
        </div>
      )}
      {params.get('xero') === 'connected' && (
        <div className="card p-4 border-tnh-ok bg-emerald-50 dark:bg-emerald-900/20 text-sm">
          Xero connected. Invoices will be pushed automatically on every successful Stripe payment.
          <button onClick={() => setParams({})} className="ml-3 text-xs text-tnh-accent hover:underline">dismiss</button>
        </div>
      )}

      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}

      {license === null && !err && <div className="card p-8 text-center text-gray-500">Loading…</div>}

      {license && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="card p-5 lg:col-span-2">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Active license</div>
            <div className="text-lg font-mono">{license.license_id}</div>
            <div className="mt-2 text-sm flex flex-wrap items-center gap-3">
              <span className="pill bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">{license.plan_name}</span>
              {license.status === 'active'
                ? <span className="pill-ok">{license.status}</span>
                : license.status === 'pending_payment'
                ? <span className="pill-warn">{license.status}</span>
                : <span className="pill-err">{license.status}</span>}
              <span className="text-gray-500">term: {license.term_years}y</span>
              <span className="text-gray-500">price: {fmtUSD(license.plan_amount, license.currency)}/yr</span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500">Started</div>
                <div className="font-medium">{fmtDate(license.start_date)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Expires</div>
                <div className="font-medium">{fmtDate(license.end_date)} <span className="text-gray-500">({daysUntil(license.end_date)}d)</span></div>
              </div>
            </div>

            <div className="mt-4">
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Modules enabled</div>
              <div className="flex flex-wrap gap-1.5">
                {(() => { try { return JSON.parse(license.modules_enabled) as string[]; } catch { return []; } })().map((m) => (
                  <span key={m} className="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{m}</span>
                ))}
              </div>
            </div>
          </div>

          {canRenew && (
            <div className="card p-5 bg-tnh-accent/[0.04] border-tnh-accent/30">
              <div className="text-xs text-tnh-accent uppercase tracking-wide font-semibold mb-2">Renew</div>
              <p className="text-sm text-gray-500 mb-3">Extend your license. Multi-year terms get a discount.</p>
              <div className="space-y-2">
                <RenewButton years={1} tenant_id={tenant_id} plan_cents={license.plan_amount} currency={license.currency} />
                <RenewButton years={3} tenant_id={tenant_id} plan_cents={license.plan_amount} currency={license.currency} discount="-10%" />
                <RenewButton years={5} tenant_id={tenant_id} plan_cents={license.plan_amount} currency={license.currency} discount="-20%" />
              </div>
              <p className="text-[11px] text-gray-500 mt-3">
                Opens Stripe Checkout. On payment, your license end date extends and a Xero invoice is created automatically.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Xero connection — super-admin only */}
      {me.role === 'super_admin' && xero && (
        <div className="card p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Xero integration</div>
              {xero.connected
                ? <div className="text-sm">Connected to <strong>{xero.tenant_name}</strong></div>
                : <div className="text-sm text-gray-500">Not connected. Connect to push invoices on every Stripe payment.</div>}
            </div>
            {!xero.connected && (
              <button onClick={async () => {
                try { const r = await api.xeroConnect(); window.location.href = r.auth_url; }
                catch (e: any) { alert(e.message); }
              }} className="btn-primary text-sm">Connect Xero</button>
            )}
          </div>
        </div>
      )}

      {/* Payment history */}
      {sessions.length > 0 && (
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">Payment history</div>
          <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark text-sm">
            {sessions.map((s) => (
              <div key={s.session_id} className="px-4 py-2 flex items-center justify-between">
                <div>
                  <div className="font-mono text-xs">{s.session_id}</div>
                  <div className="text-xs text-gray-500">{new Date(s.created_at).toLocaleString()} · {s.term_years}y · {s.initiated_by}</div>
                </div>
                <div className="text-right">
                  <div>{fmtUSD(s.amount_cents, s.currency)}</div>
                  <div className={`text-xs ${s.status === 'completed' ? 'text-emerald-600' : s.status === 'pending' ? 'text-amber-600' : 'text-tnh-err'}`}>{s.status}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent events */}
      {events.length > 0 && (
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">License events</div>
          <div className="card divide-y divide-tnh-border dark:divide-tnh-border-dark text-xs">
            {events.slice(0, 20).map((e) => (
              <div key={e.event_id} className="px-3 py-2 flex justify-between">
                <span><span className="font-mono">{e.event_type}</span> by {e.actor}</span>
                <span className="text-gray-500">{new Date(e.occurred_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RenewButton({ years, tenant_id, plan_cents, currency, discount }: { years: 1 | 3 | 5; tenant_id: string; plan_cents: number; currency: string; discount?: string }) {
  const [busy, setBusy] = useState(false);
  const discountMult = years === 3 ? 0.9 : years === 5 ? 0.8 : 1;
  const total = Math.round(plan_cents * years * discountMult);

  async function go() {
    setBusy(true);
    try {
      const r = await api.paymentsCheckout({ tenant_id, years });
      window.location.href = r.checkout_url;
    } catch (e: any) {
      alert(e.message);
    } finally { setBusy(false); }
  }

  return (
    <button onClick={go} disabled={busy} className="w-full px-4 py-3 rounded-lg border border-tnh-border dark:border-tnh-border-dark hover:bg-tnh-accent/[0.06] transition text-left flex items-center justify-between">
      <div>
        <div className="font-medium">{years} year{years > 1 ? 's' : ''}{discount ? <span className="ml-2 text-xs text-emerald-600 font-semibold">{discount}</span> : ''}</div>
        <div className="text-xs text-gray-500">{busy ? 'Opening Stripe…' : 'Click to pay & extend'}</div>
      </div>
      <div className="text-right">
        <div className="font-semibold">{fmtUSD(total, currency)}</div>
      </div>
    </button>
  );
}
